import {
  Badge,
  Button,
  Card,
  DataTable,
  TableNumericCell,
  TablePrimaryCell,
} from '@shared/components/ui';
import { api } from '@shared/services/api';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { formatBytes } from '@/shared/utils/formatBytes';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Database,
  HardDrive,
  Clock,
  Calendar,
  RotateCcw as RestoreIcon,
  X,
  CheckCircle2,
  AlertCircle,
  History,
  FileSearch,
  ArrowLeft,
  Settings,
  Play,
  Timer,
} from 'lucide-react';
import { SelectiveRestoreWizard } from '@features/restores/components/SelectiveRestoreWizard';
import { BackupJobLogsViewer } from '@features/backups/components/BackupJobLogsViewer';
import { BackupFileBrowser } from '@features/backups/components/BackupFileBrowser';
import { useDialog } from '@shared/hooks/useDialog';

interface BackupSnapshot {
  id: string;
  organization_id: string;
  backup_config_id: string;
  backup_config_name: string;
  backup_config_type: 'FILES' | 'POSTGRESQL';
  agent_name: string;
  status: string;
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  duration_seconds: number | null;
  size_bytes: number | null;
  speed_mbps: number | null;
  backup_id: string | null;
  compression_ratio: number | null;
  space_saved_bytes: number | null;
  original_size_bytes: number | null;
  is_restorable: boolean;
  source_host?: string;
  source_port?: number;
  source_database?: string;
  source_username?: string;
}

interface BackupSnapshotsResponse {
  jobs: BackupSnapshot[];
  count: number;
  total: number;
  page: number;
  page_size: number;
  timezone: string;
}

interface BackupConfig {
  id: string;
  name: string;
  description?: string;
  type: 'FILES' | 'POSTGRESQL';
  agent_id: string;
  agent_name?: string;
  agent_status?: string;
  is_enabled: boolean;
  storage_destination_name?: string;
  retention_policy?: {
    keep_daily?: number;
    keep_weekly?: number;
    keep_monthly?: number;
  };
  schedule?: {
    id: string;
    type: string;
    cron_expression?: string;
    interval_value?: string;
    timezone?: string;
    next_run_at?: string;
    is_enabled: boolean;
  };
}

export default function ConfigSnapshots() {
  const { t, i18n } = useTranslation('backups');
  const { id: configId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useDialog();

  // Data state
  const [config, setConfig] = useState<BackupConfig | null>(null);
  const [snapshots, setSnapshots] = useState<BackupSnapshot[]>([]);
  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const snapshotsRef = useRef<(background?: boolean) => Promise<void>>(async () => {});
  const snapshotIdsRef = useRef<string[]>([]);
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [total, setTotal] = useState<number | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Modal state
  const [selectedSnapshot, setSelectedSnapshot] = useState<BackupSnapshot | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [restoreAgentId, setRestoreAgentId] = useState<string>('');
  const [restoreFileSelection, setRestoreFileSelection] = useState<{
    snapshotId: string;
    files: string[];
  } | null>(null);
  const selectedFilesForRestore =
    selectedSnapshot && restoreFileSelection?.snapshotId === selectedSnapshot.id
      ? restoreFileSelection.files
      : [];

  // Fetch config details
  const fetchConfig = useCallback(async () => {
    if (!configId) return;
    try {
      const res = await api.get<BackupConfig>(`/backups/configs/${configId}`);
      setConfig(res.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [configId]);

  // Fetch snapshots
  const fetchSnapshots = useCallback(
    async (background = false) => {
      if (!configId) return;
      try {
        if (!background) {
          setLoading(true);
          setError('');
          setRefreshError('');
        }

        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('page_size', pageSize.toString());
        // CRITICAL: Always filter for restorable snapshots only
        params.append('restorable', 'true');
        params.append('status', 'COMPLETED');
        params.append('backup_config_id', configId);

        const response = await api.get<BackupSnapshotsResponse>(
          `/backup-jobs?${params.toString()}`
        );
        setSnapshots(response.data.jobs);
        setTotal(response.data.total || 0);
        setTimezone(response.data.timezone || 'UTC');
        setError('');
        setRefreshError('');
      } catch (err) {
        if (background && snapshotIdsRef.current.length > 0) {
          setRefreshError(t('snapshots.errors.refreshFailed'));
        } else {
          setError(t('snapshots.errors.loadFailed'));
        }
        console.error(err);
      } finally {
        if (!background) setLoading(false);
      }
    },
    [configId, page, t]
  );

  // Keep the poll's refs pointing at the latest values.
  snapshotsRef.current = fetchSnapshots;
  snapshotIdsRef.current = snapshots.map((s) => `${s.id}:${s.status}`);

  // Initial load: a foreground fetch, so `loading` actually clears. The poll
  // below always fetches in the background, which deliberately leaves the
  // spinner alone — so it cannot perform the first load.
  useEffect(() => {
    fetchConfig();
    fetchSnapshots();
  }, [fetchConfig, fetchSnapshots]);

  // Refresh while a snapshot may be landing. Adaptive: 5s base backing off to
  // 30s once the list stops changing, and paused while the tab is hidden.
  useAdaptivePoll({
    enabled: true,
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      await snapshotsRef.current(true);
      return snapshotIdsRef.current.join('|');
    },
    isEqual: (a, b) => a === b,
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  const getTriggerLabel = (trigger: string) => {
    switch (trigger.toLowerCase()) {
      case 'schedule':
        return t('jobs.labels.schedule');
      case 'manual':
        return t('jobs.labels.manual');
      default:
        return trigger.replace(/_/g, ' ').toLocaleUpperCase(i18n.language);
    }
  };

  const openRestore = async (snapshot: BackupSnapshot) => {
    // Check if agent is online before allowing restore
    if (!config) {
      await showError(
        t('snapshots.errors.configNotLoaded', 'Configuração não carregada'),
        t('snapshots.errors.waitForConfig', 'Aguarde o carregamento da configuração.')
      );
      return;
    }

    // Agent must be linked to this backup config
    if (!config.agent_id) {
      await showError(
        t('snapshots.errors.noAgentLinked', 'Nenhum agente vinculado'),
        t(
          'snapshots.errors.agentRequired',
          'Esta configuração de backup não possui um agente vinculado.'
        )
      );
      return;
    }

    // Check agent status - must be ONLINE for restore
    const agentStatus = config.agent_status?.toUpperCase();
    if (agentStatus !== 'ONLINE') {
      await showError(
        t('snapshots.errors.agentOffline', 'Agente Offline'),
        t(
          'snapshots.errors.agentOfflineMessage',
          'O agente "{{agentName}}" está {{status}}. O restore só pode ser executado quando o agente estiver online.',
          {
            agentName: config.agent_name || config.agent_id,
            status: config.agent_status || 'offline',
          }
        )
      );
      return;
    }

    setSelectedSnapshot(snapshot);
    setRestoreAgentId(config.agent_id);
    setShowRestoreModal(true);
  };

  const openDetails = (snapshot: BackupSnapshot) => {
    setSelectedSnapshot(snapshot);
    setShowDetailsModal(true);
  };

  const openLogs = (snapshot: BackupSnapshot) => {
    setSelectedSnapshot(snapshot);
    setShowLogsModal(true);
  };

  const openFileBrowser = (snapshot: BackupSnapshot) => {
    setSelectedSnapshot(snapshot);
    setShowFileBrowser(true);
  };

  const handleTriggerBackup = async () => {
    if (!configId) return;
    try {
      const res = await api.post(`/backups/configs/${configId}/trigger`);
      const data = res.data;

      // Check if response indicates success (has job_id or status is PENDING/QUEUED/RUNNING)
      if (
        data?.job_id &&
        (data?.status === 'PENDING' || data?.status === 'QUEUED' || data?.status === 'RUNNING')
      ) {
        await showSuccess(t('snapshots.backupTriggered'), t('snapshots.backupTriggeredMessage'));
        // Refresh after a short delay
        setTimeout(() => fetchSnapshots(), 1000);
      } else if (data?.code === 'SUBSCRIPTION_REQUIRED') {
        await showError(
          t('snapshots.subscriptionRequired'),
          `${data.error}\n\n${t('snapshots.subscribePrompt')}`
        );
      } else {
        await showError(
          t('snapshots.backupTriggerFailed'),
          data?.error || t('snapshots.backupTriggerFailedMessage')
        );
      }
    } catch (err: any) {
      console.error('Failed to trigger backup:', err);
      const errorData = err?.response?.data;
      if (errorData?.code === 'SUBSCRIPTION_REQUIRED') {
        await showError(
          t('snapshots.subscriptionRequired'),
          `${errorData.error}\n\n${t('snapshots.subscribePrompt')}`
        );
      } else {
        await showError(
          t('snapshots.backupTriggerFailed'),
          errorData?.error || err?.message || t('snapshots.backupTriggerFailedMessage')
        );
      }
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Breadcrumb & Header */}
        <div className="space-y-4">
          <button
            onClick={() => navigate('/backups/configs')}
            className="!min-h-11 flex items-center text-sm font-bold text-text-muted-light dark:text-text-muted-dark hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('snapshots.backToConfigs')}
          </button>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-2xl bg-accent-soft-light dark:bg-accent-soft-dark flex items-center justify-center text-brand-600 dark:text-brand-400">
                {config?.type === 'POSTGRESQL' ? <Database size={28} /> : <HardDrive size={28} />}
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight break-words">
                  {config?.name || t('snapshots.unknownConfig')}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant={config?.is_enabled ? 'success' : 'neutral'}>
                    {config?.is_enabled ? t('snapshots.enabled') : t('snapshots.disabled')}
                  </Badge>
                  <Badge variant="info">
                    {config?.type === 'POSTGRESQL'
                      ? t('snapshots.postgresBackup')
                      : config?.type === 'FILES'
                        ? t('snapshots.fileBackup')
                        : t('snapshots.unknown')}
                  </Badge>
                  {total !== null && (
                    <Badge variant="neutral">
                      {total} {t(total === 1 ? 'snapshots.titleSingular' : 'snapshots.title')}
                    </Badge>
                  )}
                  <span className="text-sm text-text-muted-light dark:text-text-muted-dark">
                    {t('snapshots.agent')} {config?.agent_name || t('snapshots.unknown')}
                  </span>
                </div>
                {config?.description && typeof config.description === 'string' && (
                  <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
                    {config.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex w-full flex-col sm:flex-row lg:w-auto gap-2">
              <Button
                variant="secondary"
                className="!min-h-11 w-full whitespace-nowrap lg:w-auto"
                onClick={() => navigate(`/backups/configs/${configId}/edit`)}
              >
                <Settings className="w-4 h-4 mr-2" /> {t('snapshots.editConfig')}
              </Button>
              <Button
                className="!min-h-11 w-full whitespace-nowrap lg:w-auto"
                onClick={handleTriggerBackup}
                disabled={!config?.is_enabled}
              >
                <Play className="w-4 h-4 mr-2" /> {t('snapshots.backupNow')}
              </Button>
            </div>
          </div>
        </div>

        {/* Schedule & Retention Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Next Schedule */}
          {config?.schedule && config.schedule.is_enabled && config.schedule.next_run_at && (
            <Card className="p-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center space-x-3 text-sm min-w-0">
                  <div className="flex items-center space-x-2 text-brand-600 dark:text-brand-400 shrink-0">
                    <Timer className="w-4 h-4" />
                    <span className="font-bold">{t('snapshots.nextSchedule')}</span>
                  </div>
                  <span className="text-slate-700 dark:text-slate-200 font-medium">
                    {new Intl.DateTimeFormat(i18n.language, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(new Date(config.schedule.next_run_at))}
                  </span>
                </div>
                <Badge variant="info" className="text-[10px] shrink-0 ml-2 max-w-[150px] truncate">
                  {config.schedule.type === 'CRON'
                    ? config.schedule.cron_expression
                    : config.schedule.interval_value}
                </Badge>
              </div>
            </Card>
          )}

          {/* Retention Policy */}
          {config?.retention_policy && typeof config.retention_policy === 'object' && (
            <Card className="p-3">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {t('snapshots.retention')}
                </span>
                {config.retention_policy.keep_daily && (
                  <span className="text-slate-600 dark:text-slate-300">
                    {String(config.retention_policy.keep_daily)}{' '}
                    {t(
                      config.retention_policy.keep_daily === 1
                        ? 'snapshots.daily'
                        : 'snapshots.dailyPlural'
                    )}
                  </span>
                )}
                {config.retention_policy.keep_weekly && (
                  <span className="text-slate-600 dark:text-slate-300">
                    {String(config.retention_policy.keep_weekly)}{' '}
                    {t(
                      config.retention_policy.keep_weekly === 1
                        ? 'snapshots.weekly'
                        : 'snapshots.weeklyPlural'
                    )}
                  </span>
                )}
                {config.retention_policy.keep_monthly && (
                  <span className="text-slate-600 dark:text-slate-300">
                    {String(config.retention_policy.keep_monthly)}{' '}
                    {t(
                      config.retention_policy.keep_monthly === 1
                        ? 'snapshots.monthly'
                        : 'snapshots.monthlyPlural'
                    )}
                  </span>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('snapshots.loading')}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="p-6 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20">
            <div role="alert">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-6 h-6 text-state-danger-light dark:text-state-danger-dark" />
                <p className="text-state-danger-light dark:text-state-danger-dark">{error}</p>
              </div>
              <Button variant="outline" className="mt-4 min-h-11" onClick={() => fetchSnapshots()}>
                {t('snapshots.errors.retry')}
              </Button>
            </div>
          </Card>
        )}

        {refreshError && !error && snapshots.length > 0 && (
          <Card className="p-4 bg-state-warning-bg-light dark:bg-state-warning-bg-dark border-state-warning-light/20 dark:border-state-warning-dark/20">
            <div
              role="status"
              aria-live="polite"
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <AlertCircle className="w-5 h-5 shrink-0 text-state-warning-light dark:text-state-warning-dark" />
                <p className="text-state-warning-light dark:text-state-warning-dark">
                  {refreshError}
                </p>
              </div>
              <Button
                variant="outline"
                className="min-h-11 shrink-0"
                onClick={() => fetchSnapshots(true)}
              >
                {t('snapshots.errors.retry')}
              </Button>
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && snapshots.length === 0 && (
          <Card className="py-20 text-center">
            <Database className="w-16 h-16 text-surface-border-light dark:text-surface-border-dark mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('snapshots.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 mb-6">
              {t('snapshots.empty.message')}
            </p>
            <Button onClick={handleTriggerBackup} disabled={!config?.is_enabled}>
              <Play className="w-4 h-4 mr-2" /> {t('snapshots.empty.runFirst')}
            </Button>
          </Card>
        )}

        {/* Snapshots List */}
        {!loading && !error && snapshots.length > 0 && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-black text-slate-900 dark:text-white">
                  {t('snapshots.available')}
                </h2>
                <Badge variant="success" className="h-6">
                  {t('snapshots.restorableOnly')}
                </Badge>
              </div>
              <Badge variant="info" className="h-6 max-w-full truncate">
                {t('snapshots.timezone')}: {timezone}
              </Badge>
            </div>

            {/* Compact card layout — phones and small tablets */}
            <div className="grid grid-cols-1 gap-2 lg:hidden">
              {snapshots.map((snapshot, index) => (
                <Card key={snapshot.id} className="p-0 overflow-hidden">
                  <div className="flex items-stretch">
                    <div
                      className={`w-2 ${index === 0 ? 'bg-brand-500' : 'bg-state-ok-light dark:bg-state-ok-dark'}`}
                    />
                    <div className="flex-1 p-4 space-y-4">
                      {/* Mobile header */}
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            index === 0
                              ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400'
                              : 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'
                          }`}
                        >
                          {index === 0 ? (
                            <span className="text-sm font-black">#1</span>
                          ) : (
                            <CheckCircle2 size={20} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="min-w-0 font-black text-slate-900 dark:text-white break-words">
                              {t('snapshots.snapshotNumber', {
                                number: (page - 1) * pageSize + index + 1,
                              })}
                            </h3>
                            {page === 1 && index === 0 && (
                              <Badge variant="info" className="shrink-0 text-[9px]">
                                {t('snapshots.mostRecent')}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 flex items-center space-x-2 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                            <span className="flex min-w-0 items-center">
                              <Calendar size={12} className="mr-1 shrink-0" />{' '}
                              {formatDate(snapshot.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Mobile stats grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('snapshots.columns.size')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {formatBytes(snapshot.size_bytes)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('snapshots.columns.speed')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {snapshot.speed_mbps?.toFixed(1) || '0'} MB/s
                          </p>
                        </div>
                        {snapshot.compression_ratio && (
                          <div>
                            <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                              {t('snapshots.columns.saved')}
                            </p>
                            <p className="text-sm font-black text-state-ok-light dark:text-state-ok-dark">
                              {snapshot.compression_ratio.toFixed(1)}%
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                            {t('snapshots.columns.triggered')}
                          </p>
                          <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                            {getTriggerLabel(snapshot.triggered_by)}
                          </p>
                        </div>
                      </div>

                      {/* Mobile actions */}
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetails(snapshot)}
                          aria-label={t('snapshots.actions.detailsForSnapshot', {
                            date: formatDate(snapshot.created_at),
                          })}
                          className="!min-h-11 flex-1 basis-[calc(50%-0.5rem)]"
                        >
                          {t('snapshots.actions.details')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openLogs(snapshot)}
                          aria-label={t('snapshots.actions.logsForSnapshot', {
                            date: formatDate(snapshot.created_at),
                          })}
                          className="!min-h-11 flex-1 basis-[calc(50%-0.5rem)]"
                        >
                          {t('snapshots.actions.logs')}
                        </Button>
                        {snapshot.backup_config_type === 'FILES' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openFileBrowser(snapshot)}
                            aria-label={t('snapshots.actions.browseSnapshot', {
                              date: formatDate(snapshot.created_at),
                            })}
                            title={t('snapshots.actions.browse')}
                            className="!min-h-11 min-w-11 flex-1 basis-[calc(50%-0.5rem)]"
                          >
                            <FileSearch className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => openRestore(snapshot)}
                          aria-label={t('snapshots.actions.restoreSnapshot', {
                            date: formatDate(snapshot.created_at),
                          })}
                          className="!min-h-11 flex-1 basis-[calc(50%-0.5rem)]"
                        >
                          <RestoreIcon className="w-4 h-4 mr-1" /> {t('snapshots.actions.restore')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Tabular layout — desktop. Content-sized columns; the identity
                column absorbs slack so localized labels are never clipped. */}
            <div className="hidden lg:block">
              <DataTable
                caption={t('snapshots.title')}
                rows={snapshots}
                rowKey={(snapshot) => snapshot.id}
                rowAccent={(_, index) =>
                  index === 0 ? 'bg-brand-500' : 'bg-state-ok-light dark:bg-state-ok-dark'
                }
                columns={[
                  {
                    key: 'snapshot',
                    header: t('snapshots.title'),
                    width: 'w-full',
                    cell: (snapshot, index) => (
                      <TablePrimaryCell
                        title={t('snapshots.snapshotNumber', {
                          number: (page - 1) * pageSize + index + 1,
                        })}
                        meta={
                          <>
                            <span className="flex items-center gap-1">
                              <Calendar size={12} /> {formatDate(snapshot.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {snapshot.duration_seconds || 0}s
                            </span>
                            <span className="flex items-center gap-1">
                              <HardDrive size={12} /> {snapshot.backup_id?.slice(0, 8) || 'N/A'}
                            </span>
                            {/* Badge lives on the meta line so it never competes
                                with the title for horizontal space. */}
                            {page === 1 && index === 0 && (
                              <Badge variant="info" className="text-[9px]">
                                {t('snapshots.mostRecent')}
                              </Badge>
                            )}
                          </>
                        }
                      />
                    ),
                  },
                  {
                    key: 'size',
                    header: t('snapshots.columns.size'),
                    align: 'right',
                    cell: (snapshot) => (
                      <TableNumericCell value={formatBytes(snapshot.size_bytes)} />
                    ),
                  },
                  {
                    key: 'speed',
                    header: t('snapshots.columns.speed'),
                    align: 'right',
                    cell: (snapshot) => (
                      <TableNumericCell value={(snapshot.speed_mbps ?? 0).toFixed(1)} unit="MB/s" />
                    ),
                  },
                  {
                    key: 'saved',
                    header: t('snapshots.columns.saved'),
                    align: 'right',
                    hideBelow: '2xl',
                    cell: (snapshot) =>
                      snapshot.compression_ratio ? (
                        <TableNumericCell
                          value={`${snapshot.compression_ratio.toFixed(1)}%`}
                          tone="text-state-ok-light dark:text-state-ok-dark"
                        />
                      ) : (
                        <span className="text-text-subtle-light dark:text-text-subtle-dark">—</span>
                      ),
                  },
                  {
                    key: 'triggered',
                    header: t('snapshots.columns.triggered'),
                    align: 'right',
                    hideBelow: '2xl',
                    cell: (snapshot) => (
                      <span className="whitespace-nowrap text-xs font-bold uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark">
                        {getTriggerLabel(snapshot.triggered_by)}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    align: 'right',
                    width: 'w-[300px]',
                    cell: (snapshot) => {
                      const date = formatDate(snapshot.created_at);

                      return (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="!min-h-11"
                            onClick={() => openDetails(snapshot)}
                            aria-label={t('snapshots.actions.detailsForSnapshot', { date })}
                          >
                            {t('snapshots.actions.details')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="!min-h-11"
                            onClick={() => openLogs(snapshot)}
                            aria-label={t('snapshots.actions.logsForSnapshot', { date })}
                          >
                            {t('snapshots.actions.logs')}
                          </Button>
                          {snapshot.backup_config_type === 'FILES' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="!min-h-11"
                              onClick={() => openFileBrowser(snapshot)}
                              aria-label={t('snapshots.actions.browseSnapshot', { date })}
                              title={t('snapshots.actions.browse')}
                            >
                              {t('snapshots.actions.browse')}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="!min-h-11"
                            onClick={() => openRestore(snapshot)}
                            aria-label={t('snapshots.actions.restoreSnapshot', { date })}
                          >
                            <RestoreIcon className="w-4 h-4 mr-1" />{' '}
                            {t('snapshots.actions.restore')}
                          </Button>
                        </div>
                      );
                    },
                  },
                ]}
              />
            </div>

            {/* Pagination */}
            {total !== null && total > pageSize && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                  {t('snapshots.pagination.showing', { count: snapshots.length, total })}
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="!min-h-11"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft size={16} className="mr-1" /> {t('snapshots.pagination.prev')}
                  </Button>
                  <div
                    aria-current="page"
                    className="!min-h-11 min-w-11 flex items-center justify-center px-4 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-xs font-black"
                  >
                    {page}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="!min-h-11"
                    disabled={snapshots.length < pageSize}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t('snapshots.pagination.next')} <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailsModal && selectedSnapshot && (
        <SnapshotDetailModal
          snapshot={selectedSnapshot}
          timezone={timezone}
          onClose={() => setShowDetailsModal(false)}
          onRestore={() => {
            setShowDetailsModal(false);
            setShowRestoreModal(true);
          }}
          onViewLogs={() => {
            setShowDetailsModal(false);
            setShowLogsModal(true);
          }}
          onBrowseFiles={
            selectedSnapshot.backup_config_type === 'FILES'
              ? () => {
                  setShowDetailsModal(false);
                  setShowFileBrowser(true);
                }
              : undefined
          }
        />
      )}

      {/* Restore Modal */}
      {showRestoreModal && selectedSnapshot && (
        <SelectiveRestoreWizard
          isOpen={showRestoreModal}
          onClose={() => {
            setShowRestoreModal(false);
            setRestoreAgentId('');
          }}
          backupJobId={selectedSnapshot.id}
          backupConfigName={selectedSnapshot.backup_config_name}
          backupConfigType={selectedSnapshot.backup_config_type}
          sourceHost={selectedSnapshot.source_host}
          sourcePort={selectedSnapshot.source_port}
          sourceDatabase={selectedSnapshot.source_database}
          sourceUsername={selectedSnapshot.source_username}
          backupJobSnapshotId={selectedSnapshot.backup_id}
          agentId={restoreAgentId}
          initialSelectedFiles={selectedFilesForRestore}
          onSelectedFilesChange={(files) =>
            setRestoreFileSelection({ snapshotId: selectedSnapshot.id, files })
          }
          onSuccess={() => setRestoreFileSelection(null)}
        />
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedSnapshot && (
        <BackupJobLogsViewer
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
          backupJobId={selectedSnapshot.id}
          backupConfigName={selectedSnapshot.backup_config_name}
        />
      )}

      {/* File Browser Modal */}
      {showFileBrowser && selectedSnapshot && (
        <BackupFileBrowser
          isOpen={showFileBrowser}
          onClose={() => setShowFileBrowser(false)}
          backupJobId={selectedSnapshot.id}
          backupConfigName={selectedSnapshot.backup_config_name}
          initialSelectedFiles={selectedFilesForRestore}
          onSelectFiles={(files) => {
            setRestoreFileSelection({ snapshotId: selectedSnapshot.id, files });
            setShowFileBrowser(false);
            setShowRestoreModal(true);
          }}
        />
      )}
    </>
  );
}

// Detail Modal Component
interface DetailModalProps {
  snapshot: BackupSnapshot;
  timezone: string;
  onClose: () => void;
  onRestore: () => void;
  onViewLogs: () => void;
  onBrowseFiles?: () => void;
}

function SnapshotDetailModal({
  snapshot,
  onClose,
  onRestore,
  onViewLogs,
  onBrowseFiles,
}: DetailModalProps) {
  const { t, i18n } = useTranslation('backups');
  const formatDate = (date: string | null) =>
    date
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(date)
        )
      : '-';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="snapshot-detail-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
    >
      <div className="my-auto w-full max-w-3xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-elevated p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-2xl flex items-center justify-center text-state-ok-light dark:text-state-ok-dark">
              <CheckCircle2 size={28} />
            </div>
            <div>
              <h2
                id="snapshot-detail-title"
                className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
              >
                {snapshot.backup_config_name}
              </h2>
              <div className="flex items-center space-x-2 mt-1">
                <Badge variant="success">{t('snapshots.restorable')}</Badge>
                <span className="text-sm text-text-muted-light dark:text-text-muted-dark">
                  {snapshot.backup_config_type === 'POSTGRESQL'
                    ? t('snapshots.postgresBackup')
                    : t('snapshots.fileBackup')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:actions.close')}
            title={t('common:actions.close')}
            className="!min-h-11 min-w-11 flex items-center justify-center p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-text-subtle-light dark:text-text-subtle-dark" />
          </button>
        </div>

        {/* Snapshot Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          <DetailItem
            label={t('snapshots.details.snapshotId')}
            value={String(snapshot.backup_id || '-')}
            mono
          />
          <DetailItem label={t('snapshots.details.jobId')} value={String(snapshot.id)} mono />
          <DetailItem
            label={t('snapshots.details.created')}
            value={formatDate(snapshot.created_at)}
          />
          <DetailItem
            label={t('snapshots.details.started')}
            value={formatDate(snapshot.started_at)}
          />
          <DetailItem
            label={t('snapshots.details.completed')}
            value={formatDate(snapshot.completed_at)}
          />
          <DetailItem
            label={t('snapshots.details.duration')}
            value={t('snapshots.durationSeconds', { count: snapshot.duration_seconds || 0 })}
          />
        </div>

        {/* Storage Stats */}
        <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl">
          <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight mb-4">
            {t('snapshots.details.storagePerformance')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                {t('snapshots.details.finalSize')}
              </p>
              <p className="text-xl font-black text-slate-900 dark:text-white">
                {formatBytes(snapshot.size_bytes)}
              </p>
            </div>
            {snapshot.original_size_bytes && (
              <div>
                <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                  {t('snapshots.details.original')}
                </p>
                <p className="text-xl font-black text-slate-700 dark:text-slate-200">
                  {formatBytes(snapshot.original_size_bytes)}
                </p>
              </div>
            )}
            {snapshot.space_saved_bytes && (
              <div>
                <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                  {t('snapshots.details.saved')}
                </p>
                <p className="text-xl font-black text-state-ok-light dark:text-state-ok-dark">
                  {formatBytes(snapshot.space_saved_bytes)}
                </p>
              </div>
            )}
            {snapshot.compression_ratio && (
              <div>
                <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                  {t('snapshots.details.compression')}
                </p>
                <p className="text-xl font-black text-state-ok-light dark:text-state-ok-dark">
                  {snapshot.compression_ratio.toFixed(1)}%
                </p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                {t('snapshots.details.speed')}
              </p>
              <p className="text-xl font-black text-slate-900 dark:text-white">
                {snapshot.speed_mbps?.toFixed(1) || '0'} MB/s
              </p>
            </div>
          </div>
        </div>

        {/* Source Info for PostgreSQL */}
        {snapshot.backup_config_type === 'POSTGRESQL' && (
          <div className="p-6 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl">
            <h3 className="text-sm font-black text-state-info-light dark:text-state-info-dark uppercase tracking-tight mb-4">
              {t('snapshots.details.sourceDatabase')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <DetailItem label={t('snapshots.details.host')} value={snapshot.source_host || '-'} />
              <DetailItem
                label={t('snapshots.details.port')}
                value={snapshot.source_port?.toString() || '-'}
              />
              <DetailItem
                label={t('snapshots.details.database')}
                value={snapshot.source_database || '-'}
              />
              <DetailItem
                label={t('snapshots.details.username')}
                value={snapshot.source_username || '-'}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
          <Button variant="secondary" className="!min-h-11 flex-1" onClick={onViewLogs}>
            <History size={16} className="mr-2" /> {t('snapshots.actions.logs')}
          </Button>
          {onBrowseFiles && (
            <Button variant="secondary" className="!min-h-11 flex-1" onClick={onBrowseFiles}>
              <FileSearch size={16} className="mr-2" /> {t('snapshots.actions.browse')}
            </Button>
          )}
          <Button className="!min-h-11 flex-1" onClick={onRestore}>
            <RestoreIcon size={16} className="mr-2" /> {t('snapshots.actions.restoreFrom')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em] mb-1.5">
        {label}
      </p>
      <p
        className={`text-sm font-bold text-slate-900 dark:text-white ${mono ? 'font-mono break-all' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}
